import type { RuleListItem, RuleVersionItem } from "@wallyo/contracts/console/configuration";
import type { FormEvent, ReactNode } from "react";
import { ruleCategoryName, ruleName, t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { configHref } from "../config-route";
import { WriteFailureNotice } from "../team/TeamDialogs";
import type { WriteFailure } from "../team/team-format";
import {
  formatRuleValue,
  ruleInput,
  valueInputLabel,
  valueKindLabel,
  versionState,
  type RuleInput,
  type RuleVersionFields,
} from "./rules-model";

function Sensitivity({ level }: { level: RuleListItem["sensitivity"] }) {
  return <span className={`sens ${level}`}>{t(`team.sensitivity.${level}`)}</span>;
}

// ── The list ─────────────────────────────────────────────────────────────

/** Every rule: its value now, in its unit, and the next version when one is scheduled. */
export function RuleList({ rules, selectedId }: { rules: readonly RuleListItem[]; selectedId: string | null }) {
  return (
    <div className="card">
      <div className="rows rule-rows">
        {rules.map((rule) => (
          <a
            key={rule.ruleId}
            href={configHref("rules", rule.ruleId)}
            className={rule.ruleId === selectedId ? "sel" : undefined}
            aria-current={rule.ruleId === selectedId ? "true" : undefined}
            data-rule={rule.ruleId}
          >
            <span>
              {ruleName(rule.ruleId)}
              <Sensitivity level={rule.sensitivity} />
              <br />
              <span className="meta-id">{ruleCategoryName(rule.category)}</span>
              {rule.next ? (
                <>
                  <br />
                  <span className="pill info">
                    {t("rules.next", {
                      date: formatDateTime(rule.next.effectiveFrom),
                      value: formatRuleValue(rule, rule.next.value),
                    })}
                  </span>
                </>
              ) : null}
            </span>
            <b className="rule-value">{formatRuleValue(rule, rule.current.value)}</b>
          </a>
        ))}
      </div>
    </div>
  );
}

// ── One rule ─────────────────────────────────────────────────────────────

export type RuleDetailProps = {
  rule: RuleListItem;
  versions: readonly RuleVersionItem[];
  lock: "critical" | "retired" | null;
  roles: readonly string[];
  now: Date;
  onNew: () => void;
  /** The new version form, when open. */
  editor?: ReactNode;
};

function StateCell({ rule, version, now }: { rule: RuleListItem; version: RuleVersionItem; now: Date }) {
  const date = formatDateTime(version.effectiveFrom);
  switch (versionState(rule, version, now)) {
    case "scheduled":
      return <span className="pill info">{t("rules.status.scheduled", { date })}</span>;
    case "current":
      return (
        <>
          <span className="pill ok">{t("rules.status.current")}</span>{" "}
          <span className="nowrap">{t("rules.status.since", { date })}</span>
        </>
      );
    case "past":
      return (
        <>
          <span className="pill mute">{t("rules.status.past")}</span> <span className="nowrap">{date}</span>
        </>
      );
  }
}

export function RuleDetail(props: RuleDetailProps) {
  const { rule, versions, lock } = props;
  const input = ruleInput(rule.valueType);
  return (
    <div className="card" data-detail={rule.ruleId}>
      <div>
        <h3>
          {ruleName(rule.ruleId)}
          <Sensitivity level={rule.sensitivity} />
        </h3>
        <p className="sub">
          <span className="mono">{rule.ruleId}</span> · {valueKindLabel(input)}
        </p>
      </div>
      <div className="rule-now">
        <span className="meta-id">{t("rules.now")}</span>
        <b>{formatRuleValue(rule, rule.current.value)}</b>
        {rule.current.source === "default" ? <span className="pill mute">{t("rules.default")}</span> : null}
        {rule.next ? (
          <span className="pill info">
            {t("rules.next", {
              date: formatDateTime(rule.next.effectiveFrom),
              value: formatRuleValue(rule, rule.next.value),
            })}
          </span>
        ) : null}
      </div>
      <p className="foot">
        {rule.sensitivity === "critical"
          ? t("rules.sensitivity.critical")
          : t("rules.sensitivity.open", { level: t(`team.sensitivity.${rule.sensitivity}`) })}
        {rule.requiresLegalBasis ? ` ${t("rules.legalBasis.required")}` : ""}
      </p>
      {lock ? (
        <div className="denied config-lock" role="status">
          🔒{" "}
          {lock === "critical"
            ? t("rules.locked.critical", { roles: props.roles.join(", ") })
            : t("rules.locked.retired")}
        </div>
      ) : null}
      <h2>{t("rules.history")}</h2>
      {versions.length === 0 ? (
        <p className="foot">{t("rules.history.none", { value: formatRuleValue(rule, rule.defaultValue) })}</p>
      ) : (
        <div className="table-wrap">
          <table className="config-values">
            <thead>
              <tr>
                <th className="num">{t("rules.col.version")}</th>
                <th>{t("rules.col.value")}</th>
                <th>{t("rules.col.effective")}</th>
                <th>{t("rules.col.reason")}</th>
                <th>{t("rules.col.legalBasis")}</th>
                <th>{t("rules.col.by")}</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((version) => (
                <tr
                  key={version.ruleVersionId}
                  className={versionState(rule, version, props.now) === "current" ? "sel" : undefined}
                  data-version={version.version}
                >
                  <td className="num">{version.version}</td>
                  <td>
                    <b>{formatRuleValue(rule, version.value)}</b>
                  </td>
                  <td>
                    <StateCell rule={rule} version={version} now={props.now} />
                  </td>
                  <td>
                    <bdi>{version.reason}</bdi>
                  </td>
                  <td>
                    {version.legalBasis ? <bdi>{version.legalBasis}</bdi> : <span className="muted-cell">—</span>}
                  </td>
                  <td>
                    <bdi>{version.setBy.name}</bdi>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {props.editor ??
        (lock ? null : (
          <div className="btns">
            <button className="btn primary" type="button" onClick={props.onNew}>
              {t("rules.new")}
            </button>
          </div>
        ))}
    </div>
  );
}

// ── A new version ────────────────────────────────────────────────────────

export type RuleVersionFormViewProps = {
  rule: RuleListItem;
  fields: RuleVersionFields;
  onChange: (change: Partial<RuleVersionFields>) => void;
  /** The earliest date and time the input offers, as a `datetime-local` value. */
  minEffective: string;
  pending: boolean;
  problem: string | null;
  failure: WriteFailure | null;
  onSubmit: () => void;
  onCancel: () => void;
  onReload: () => void;
};

/** The value input for the rule's value type: an amount, a duration, a number, yes or no, a choice or text. */
export function RuleValueInput({
  input,
  value,
  onChange,
}: {
  input: RuleInput;
  value: string;
  onChange: (value: string) => void;
}) {
  switch (input.kind) {
    case "boolean":
      return (
        <select id="rule-value" data-kind="boolean" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="true">{t("rules.value.true")}</option>
          <option value="false">{t("rules.value.false")}</option>
        </select>
      );
    case "choice":
      return (
        <select id="rule-value" data-kind="choice" value={value} onChange={(event) => onChange(event.target.value)}>
          {input.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    case "text":
      return (
        <input
          id="rule-value"
          data-kind="text"
          maxLength={input.maxLength ?? undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    default:
      return (
        <input
          id="rule-value"
          data-kind={input.kind}
          type="number"
          inputMode="numeric"
          dir="ltr"
          min={input.min ?? undefined}
          step={input.kind === "number" && !input.integer ? "any" : 1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

export function RuleVersionFormView(props: RuleVersionFormViewProps) {
  const { rule, fields } = props;
  const input = ruleInput(rule.valueType);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <form className="config-form" onSubmit={submit} noValidate aria-labelledby="rule-form-title">
      <h2 id="rule-form-title">{t("rules.form.title")}</h2>
      <div className="form">
        <div className="field">
          <label htmlFor="rule-value">{valueInputLabel(input)}</label>
          <RuleValueInput input={input} value={fields.value} onChange={(value) => props.onChange({ value })} />
        </div>
        <fieldset className="field effective">
          <legend>{t("rules.form.effective")}</legend>
          <label>
            <input
              type="radio"
              name="rule-effective"
              value="now"
              checked={fields.effective === "now"}
              onChange={() => props.onChange({ effective: "now" })}
            />{" "}
            {t("rules.form.now")}
          </label>
          <label>
            <input
              type="radio"
              name="rule-effective"
              value="at"
              checked={fields.effective === "at"}
              onChange={() => props.onChange({ effective: "at" })}
            />{" "}
            {t("rules.form.at")}
          </label>
          {fields.effective === "at" ? (
            <input
              id="rule-effective-at"
              type="datetime-local"
              aria-label={t("rules.form.atLabel")}
              min={props.minEffective}
              value={fields.effectiveAt}
              onChange={(event) => props.onChange({ effectiveAt: event.target.value })}
            />
          ) : null}
        </fieldset>
        <div className="field">
          <label htmlFor="rule-reason">{t("rules.form.reason")}</label>
          <input
            id="rule-reason"
            value={fields.reason}
            onChange={(event) => props.onChange({ reason: event.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="rule-legal-basis">
            {t(rule.requiresLegalBasis ? "rules.form.legalBasis" : "rules.form.legalBasisOptional")}
          </label>
          <input
            id="rule-legal-basis"
            placeholder={t("rules.form.legalBasisPlaceholder")}
            aria-required={rule.requiresLegalBasis}
            value={fields.legalBasis}
            onChange={(event) => props.onChange({ legalBasis: event.target.value })}
          />
        </div>
      </div>
      <p className="foot">{t("rules.form.version", { version: rule.version })}</p>
      {props.problem ? <p className="field-problem">{props.problem}</p> : null}
      {props.failure ? <WriteFailureNotice failure={props.failure} onReload={props.onReload} /> : null}
      <div className="btns">
        <button className="btn quiet" type="button" onClick={props.onCancel}>
          {t("rules.form.cancel")}
        </button>
        <button className="btn primary" type="submit" disabled={props.pending}>
          {props.pending ? t("rules.form.sending") : t("rules.form.submit")}
        </button>
      </div>
    </form>
  );
}
